import React from 'react'
import {
    Image,
    Box,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { buildCdnImageUrl, canOptimizeImage } from '../../../../utils/image-cdn-utils'

/**
 * Default responsive image widths for different contexts
 * These match typical Chakra UI breakpoints: base (mobile), md (tablet), lg (desktop)
 */
const DEFAULT_WIDTHS = {
    HERO: [768, 1024, 1920],       // Full width hero images
    CONTENT: [400, 800, 1200],     // Content images
    TILE: [300, 400, 500],         // Category/product tiles
    THUMBNAIL: [100, 150, 200]     // Small thumbnails
}

/**
 * Default sizes attributes for different contexts
 * Tells the browser how wide the image will be at different breakpoints
 */
const DEFAULT_SIZES = {
    HERO: '100vw',                                           // Full width on all devices
    CONTENT: '(max-width: 767px) 100vw, (max-width: 1023px) 80vw, 60vw', // Varies by device
    TILE: '(max-width: 767px) 50vw, (max-width: 1023px) 33vw, 25vw',    // Grid items
    THUMBNAIL: '(max-width: 767px) 100px, 150px'           // Fixed small sizes
}

const ImageFile = ({
    Url,
    AltText = null,
    customMediaContainerStyling,
    customMediaStyling,
    preset = 'CONTENT',  // Default preset type
    widths = null,       // Custom widths array [mobile, tablet, desktop]
    sizes = null,        // Custom sizes attribute
    cdnOptions = {}      // Additional CDN options (quality, format, fit, etc.)
}) => {
    const styles = useMultiStyleConfig('ImageFile')

    // Determine which widths to use
    const imageWidths = widths || DEFAULT_WIDTHS[preset] || DEFAULT_WIDTHS.CONTENT

    // Determine which sizes attribute to use
    const imageSizes = sizes || DEFAULT_SIZES[preset] || DEFAULT_SIZES.CONTENT

    // Generate srcSet for responsive images
    const getSrcSet = () => {
        if (!canOptimizeImage(Url)) {
            return undefined
        }

        return imageWidths
            .map(width => {
                const url = buildCdnImageUrl(Url, {
                    width,
                    quality: 85,
                    format: 'auto',
                    ...cdnOptions
                })
                return `${url} ${width}w`
            })
            .join(', ')
    }

    // Generate fallback src (use smallest width)
    const getFallbackSrc = () => {
        if (!canOptimizeImage(Url)) {
            return Url
        }

        const smallestWidth = Math.min(...imageWidths)
        return buildCdnImageUrl(Url, {
            width: smallestWidth,
            quality: 85,
            format: 'auto',
            ...cdnOptions
        })
    }

    return (
        <Box {...customMediaContainerStyling} data-block-type="ImageFile">
            <Image
                src={getFallbackSrc()}
                srcSet={getSrcSet()}
                sizes={imageSizes}
                {...styles.img}
                {...customMediaStyling}
                alt={AltText || 'Image'}
                loading="lazy"
            />
        </Box>
    )
}

ImageFile.propTypes = {
    Url: PropTypes.string.isRequired,
    AltText: PropTypes.string,
    customMediaContainerStyling: PropTypes.object,
    customMediaStyling: PropTypes.object,
    preset: PropTypes.string,
    widths: PropTypes.arrayOf(PropTypes.number),
    sizes: PropTypes.string,
    cdnOptions: PropTypes.object
}

export default ImageFile
